<template>
  <div class="min-h-screen flex flex-col bg-background text-text">
    <!-- Same Header as MainLayout -->
    <header class="bg-background-light border-b border-background-lighter">
      <div class="container mx-auto px-4 py-3">
        <div class="flex items-center justify-between w-full">
          <!-- Logo with proper left alignment and increased size -->
          <div class="flex-shrink-0 bg-background-light border-background-lighter lg:hidden">
            <div class="flex items-center justify-between px-4 py-3">
              <button
                  @click="toggleMobileMenu"
                  class="p-2 rounded-md hover:bg-background-lighter transition-colors"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
              </button>
            </div>
          </div>

          <router-link to="/" class="flex items-center">
            <img
                src="/logo_small.png"
                alt="Eskrag"
                class="h-10 w-10 mr-3"
                @error="handleImageError"
            />
            <span class="text-primary text-2xl font-bold">Eskrag</span>
          </router-link>

          <!-- Search and actions -->
          <div class="flex items-center space-x-4">
            <!-- Search bar -->
            <div class="relative hidden md:block">
              <input
                  type="text"
                  placeholder="Search products..."
                  class="w-64 bg-background border border-background-lighter rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  v-model="searchQuery"
                  @keyup.enter="handleSearch"
              >
              <button
                  class="absolute right-3 top-2.5 text-text-secondary"
                  @click="handleSearch"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Theme toggle -->
            <theme-toggle />

            <!-- Price Visibility Toggle -->
            <button
                v-if="authStore.isLoggedIn"
                @click="togglePriceVisibility"
                class="p-2 text-text-secondary hover:text-text hover:bg-background-lighter rounded-full transition-colors"
                :title="isPricesHidden ? 'Show Prices' : 'Hide Prices'"
            >
              <svg v-if="isPricesHidden" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>

            <!-- Cart Button -->
            <button
                v-if="!dataStore.shouldHideCart"
                @click="cartStore.toggleCart"
                class="relative p-2 text-text-secondary hover:text-text hover:bg-background-lighter rounded-full transition-colors"
                aria-label="Shopping Cart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span
                  v-if="cartStore.count > 0"
                  class="absolute -top-1 -right-1 bg-primary text-white rounded-full text-xs w-5 h-5 flex items-center justify-center"
              >
                {{ cartStore.count }}
              </span>
            </button>

            <!-- User actions -->
            <div class="flex items-center">
              <!-- User dropdown -->
              <div class="relative user-menu">
                <button
                    @click="showUserMenu = !showUserMenu"
                    class="flex items-center space-x-2 bg-background-light hover:bg-background-lighter px-3 py-2 rounded-md"
                >
                  <span class="text-sm hidden md:inline">{{ authStore.currentUser }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-text-secondary" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                </button>

                <!-- Dropdown menu -->
                <div
                    v-if="showUserMenu"
                    class="absolute right-0 mt-2 w-48 bg-background border border-background-lighter rounded-md shadow-lg z-10"
                >
                  <div class="py-1">
                    <router-link
                        to="/panel/profile"
                        class="block px-4 py-2 text-sm hover:bg-background-lighter"
                        @click="showUserMenu = false"
                    >
                      Profile
                    </router-link>

                    <div v-if="!dataStore.shouldUseDAPricingGetter && !dataStore.shouldHideWallet" class="block px-4 py-2 text-sm text-text-secondary border-t border-background-lighter">
                      Wallet: {{ authStore.formattedWallet }}
                    </div>
                    <button @click="handleLogout" class="block w-full text-left px-4 py-2 text-sm hover:bg-background-lighter text-red-500">
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Layout Container -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Mobile Backdrop -->
      <div
          v-if="isMobileMenuOpen"
          class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          @click="closeMobileMenu"
      ></div>

      <!-- Sidebar - Full Height -->
      <div
          data-sidebar
          :class="[
          'fixed inset-y-0 left-0 z-50 w-64 bg-background-light shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col lg:z-auto',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        ]"
      >
        <!-- User Info -->
        <div class="p-4 border-b border-background-lighter flex-shrink-0">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <span class="text-white font-semibold">{{ userInitials }}</span>
            </div>
            <div>
              <p class="font-medium text-text-primary">{{ user?.name || 'User' }}</p>
              <p class="text-sm text-text-secondary">{{ user?.email || 'user@example.com' }}</p>

            </div>
          </div>
        </div>



        <!-- Navigation Menu - Flexible grow -->
        <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
          <template v-for="item in availableMenuItems" :key="item.name">
            <router-link
                :to="item.path"
                :class="[
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                  $route.path === item.path
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:bg-background-lighter hover:text-text-primary'
                ]"
                @click="closeMobileMenu"
            >
              <component :is="item.icon" class="w-5 h-5 mr-3" />
              {{ item.name }}
            </router-link>

            <!-- Show locked indicator for restricted items -->
            <div
                v-if="item.requiresActive && !isUserActive"
                class="flex items-center px-3 py-2 text-sm font-medium rounded-md text-text-secondary opacity-50 cursor-not-allowed"
                :title="`${item.name} requires account activation`"
            >
              <component :is="item.icon" class="w-5 h-5 mr-3" />
              {{ item.name }}
              <svg class="w-4 h-4 ml-auto text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </template>
        </nav>
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Page Content - Scrollable -->
        <main class="flex-1 overflow-auto bg-background">
          <div class="p-4 lg:p-6">

            <router-view />
          </div>
        </main>
      </div>
    </div>

    <!-- Include Cart and Footer components -->
    <Cart v-if="!dataStore.shouldHideCart" />

  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useDataStore } from '../stores/dataStore';
import { useCartStore } from '../stores/cartStore';
import { updateUserPreferences } from '../services/api';
import ThemeToggle from '../components/ui/ThemeToggle.vue';
import Cart from '../components/Cart.vue';
import Swal from 'sweetalert2';

// Import Heroicons
import {
  UserIcon,
  ClockIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  WalletIcon
} from '@heroicons/vue/24/outline';

export default {
  name: 'UserPanelLayout',
  components: {
    ThemeToggle,
    Cart,
    UserIcon,
    ClockIcon,
    ShoppingBagIcon,
    CreditCardIcon,
    WalletIcon
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const dataStore = useDataStore();
    const cartStore = useCartStore();

    const isMobileMenuOpen = ref(false);
    const searchQuery = ref('');
    const showUserMenu = ref(false);
    const isUpdatingPreference = ref(false);

    // All menu items with their requirements
    const allMenuItems = [
      {
        name: 'Profile',
        path: '/panel/profile',
        icon: 'UserIcon',
        requiresActive: false
      },
      {
        name: 'Orders',
        path: '/panel/orders',
        icon: 'ShoppingBagIcon',
        requiresActive: false
      },
      {
        name: 'Transaction History',
        path: '/panel/transactions',
        icon: 'CreditCardIcon',
        requiresActive: false
      },
      {
        name: 'Login History',
        path: '/panel/login-history',
        icon: 'ClockIcon',
        requiresActive: false
      },
      {
        name: 'Payment History',
        path: '/panel/payment-history',
        icon: 'ClockIcon',
        requiresActive: true
      },
      {
        name: 'Charge Wallet',
        path: '/panel/charge-wallet',
        icon: 'WalletIcon',
        requiresActive: true,
        requiresHasWallet: true
      }
    ];

    const user = computed(() => authStore.user);

    // Check if user is active
    const isUserActive = computed(() => {
      return authStore.user && authStore.user.is_active === "1";
    });

    // Filter menu items based on user status
    const availableMenuItems = computed(() => {
      return allMenuItems.filter(item => {
        if (item.requiresActive && !isUserActive.value) {
          return false;
        }
        if(item.requiresHasWallet && dataStore.shouldHideWallet)
          return false;
        return true;
      });
    });

    // Show activation notice if user accessed restricted route
    const showActivationNotice = computed(() => {
      return route.query.error === 'account_not_active';
    });

    const userInitials = computed(() => {
      const name = user.value?.name || 'U';
      return name.charAt(0).toUpperCase();
    });

    const pageTitle = computed(() => {
      const currentItem = allMenuItems.find(item => item.path === route.path);
      return currentItem?.name || 'Dashboard';
    });

    // Get user's hide_price preference
    const isPricesHidden = computed(() => {
      if (!authStore.user) return false;
      return authStore.user.hide_price === 1;
    });

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
    };

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false;
    };

    // Handle search functionality
    const handleSearch = () => {
      if (searchQuery.value.trim() === '') return;
      router.push({ name: 'search', query: { q: searchQuery.value } });
      searchQuery.value = '';
    };

    // Handle image loading errors
    const handleImageError = (event) => {
      event.target.src = '/placeholder-image.png';
    };

    // Toggle price visibility using the API service
    const togglePriceVisibility = async () => {
      if (isUpdatingPreference.value || !authStore.user) return;

      try {
        isUpdatingPreference.value = true;

        const newHidePrice = authStore.user.hide_price === 1 ? 0 : 1;
        const response = await updateUserPreferences({ hide_price: newHidePrice });

        if (response && response.user) {
          authStore.user = {
            ...authStore.user,
            hide_price: response.user.hide_price
          };

          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
        }
      } catch (error) {
        console.error('Failed to update price visibility:', error);
      } finally {
        isUpdatingPreference.value = false;
      }
    };

    const handleLogout = async () => {
      try {
        const result = await Swal.fire({
          title: 'Logout Confirmation',
          text: 'Are you sure you want to logout?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3b82f6',
          cancelButtonColor: '#6b7280',
          confirmButtonText: 'Yes, logout',
          cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
          await authStore.logout();
          showUserMenu.value = false;
          router.push('/login');

          Swal.fire({
            title: 'Logged Out',
            text: 'You have been successfully logged out.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Logout error:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to logout. Please try again.',
          icon: 'error'
        });
      }
    };

    onMounted(() => {
      const handleClickOutside = (event) => {
        if (isMobileMenuOpen.value && !event.target.closest('[data-sidebar]') && !event.target.closest('button')) {
          closeMobileMenu();
        }

        if (showUserMenu.value && !event.target.closest('.user-menu')) {
          showUserMenu.value = false;
        }
      };

      document.addEventListener('click', handleClickOutside);

      onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
      });
    });

    return {
      isMobileMenuOpen,
      searchQuery,
      showUserMenu,
      allMenuItems,
      availableMenuItems,
      user,
      userInitials,
      pageTitle,
      isPricesHidden,
      isUserActive,
      showActivationNotice,
      authStore,
      dataStore,
      cartStore,
      toggleMobileMenu,
      closeMobileMenu,
      handleSearch,
      handleImageError,
      togglePriceVisibility,
      handleLogout
    };
  }
}
</script>

<style scoped>
.user-menu {
  position: relative;
  z-index: 60;
}

@media (max-width: 1024px) {
  [data-sidebar] {
    transition: transform 0.3s ease-in-out;
  }
}

header .container {
  max-width: 100%;
}

header a {
  margin-right: auto;
}
</style>
